import { createTheme } from "@mui/material/styles";

declare module "@mui/material/styles" {
    interface Palette {
        tertiary: {
            main: string;
            light: string;
            dark: string;
            contrastText: string;
        };
    }

    interface PaletteOptions {
        tertiary?: {
            main: string;
            light: string;
            dark: string;
            contrastText: string;
        };
    }
}

// Create MUI theme with standardized modal overlay configuration (80% opacity of #071741)
const muiTheme = createTheme({
    breakpoints: {
        values: {
            xs: 0, // Mobile: < 600px (1 card per row)
            sm: 600, // Small: 600px-1279px (2 cards per row)
            md: 1280, // Medium: 1280px-1599px (3 cards per row)
            lg: 1600, // Large: ≥1600px (4 cards per row)
            xl: 1920 // Extra large: preserved for future use
        }
    },
    palette: {
        mode: "light",
        primary: {
            main: "#103699",
            light: "#4F7AD3",
            dark: "#0B2966",
            contrastText: "#ffffff"
        },
        secondary: {
            main: "#E3F2FD",
            light: "#F3F9FF",
            dark: "#B3D9F7",
            contrastText: "#103699"
        },
        tertiary: {
            main: "#FFC107",
            light: "#FFD54F",
            dark: "#F57C00",
            contrastText: "#212121"
        },
        background: {
            default: "#ffffff",
            paper: "#ffffff"
        },
        text: {
            primary: "#212121",
            secondary: "#757575"
        },
        error: {
            main: "#AC1739",
            light: "#D32F2F",
            dark: "#7B1026",
            contrastText: "#ffffff"
        },
        warning: {
            main: "#FFC107",
            light: "#FFD54F",
            dark: "#F57C00",
            contrastText: "#212121"
        },
        success: {
            main: "#4CAF50",
            light: "#81C784",
            dark: "#388E3C",
            contrastText: "#ffffff"
        },
        info: {
            main: "#2196F3",
            light: "#64B5F6",
            dark: "#1976D2",
            contrastText: "#ffffff"
        }
    },
    typography: {
        fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
        fontSize: 14,
        h1: {
            fontSize: "2rem",
            fontWeight: 500,
            lineHeight: 1.5,
            color: "#212121"
        },
        h2: {
            fontSize: "1.75rem",
            fontWeight: 500,
            lineHeight: 1.5,
            color: "#212121"
        },
        h3: {
            fontSize: "1.5rem",
            fontWeight: 500,
            lineHeight: 1.5,
            color: "#212121"
        },
        h4: {
            fontSize: "1.25rem",
            fontWeight: 500,
            lineHeight: 1.5,
            color: "#212121"
        },
        h5: {
            fontSize: "1.125rem",
            fontWeight: 500,
            lineHeight: 1.5,
            color: "#212121"
        },
        h6: {
            fontSize: "1rem",
            fontWeight: 500,
            lineHeight: 1.5,
            color: "#212121"
        },
        body1: {
            fontSize: "1rem",
            fontWeight: 500,
            lineHeight: 1.5,
            color: "#212121"
        },
        body2: {
            fontSize: "0.875rem",
            fontWeight: 500,
            lineHeight: 1.5,
            color: "#212121"
        },
        button: {
            fontSize: "0.875rem",
            fontWeight: 500,
            lineHeight: 1.75,
            textTransform: "none"
        },
        caption: {
            fontSize: "0.75rem",
            fontWeight: 500,
            lineHeight: 1.5,
            color: "#757575"
        }
    },
    spacing: 8,
    shape: {
        borderRadius: 8
    },
    components: {
        // ✅ UPDATED: STANDARDIZED MODAL OVERLAY CONFIGURATION - 80% OPACITY OF #071741 (BLUE/900)
        // Applies 80% opacity of deep blue color (#071741) to all backdrop components
        MuiBackdrop: {
            styleOverrides: {
                root: {
                    // Deep blue/900 color with 80% opacity - Strong, professional overlay
                    backgroundColor: "rgba(7, 23, 65, 0.80)",
                    // Subtle blur for enhanced content separation
                    backdropFilter: "blur(2px)",
                    // Ensure click-through behavior is preserved
                    "&.MuiBackdrop-invisible": {
                        backgroundColor: "transparent",
                        backdropFilter: "none"
                    }
                }
            }
        },
        // Apply standardized overlay to Dialog components
        MuiDialog: {
            styleOverrides: {
                root: {
                    // Ensure proper z-index stacking
                    "& .MuiBackdrop-root:not(.MuiBackdrop-invisible)": {
                        backgroundColor: "rgba(7, 23, 65, 0.80)",
                        backdropFilter: "blur(2px)"
                    }
                }
            }
        },
        // Apply standardized overlay to Modal components.
        // Menus/Popovers mount through MuiModal with an *invisible* backdrop —
        // they must not dim the page, so the overlay only targets visible backdrops.
        MuiModal: {
            styleOverrides: {
                root: {
                    "& .MuiBackdrop-root:not(.MuiBackdrop-invisible)": {
                        backgroundColor: "rgba(7, 23, 65, 0.80)",
                        backdropFilter: "blur(2px)"
                    }
                }
            }
        },
        // Apply standardized overlay to Drawer components
        MuiDrawer: {
            styleOverrides: {
                root: {
                    "& .MuiBackdrop-root:not(.MuiBackdrop-invisible)": {
                        backgroundColor: "rgba(7, 23, 65, 0.80)",
                        backdropFilter: "blur(2px)"
                    }
                }
            }
        },
        // Standard button configurations
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    padding: "8px 16px",
                    boxShadow: "none",
                    "&:hover": {
                        boxShadow: "none"
                    }
                },
                contained: {
                    "&:hover": {
                        backgroundColor: "#0B2966"
                    }
                },
                outlined: {
                    borderColor: "#103699",
                    color: "#103699",
                    "&:hover": {
                        backgroundColor: "rgba(16, 54, 153, 0.04)",
                        borderColor: "#103699"
                    }
                }
            }
        },
        // Card component styling
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12), 0px 1px 2px rgba(0, 0, 0, 0.24)",
                    border: "1px solid rgba(0, 0, 0, 0.1)"
                }
            }
        },
        // Paper component styling
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: 8
                },
                elevation1: {
                    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12), 0px 1px 2px rgba(0, 0, 0, 0.24)"
                }
            }
        },
        // TextField styling for consistency
        MuiTextField: {
            styleOverrides: {
                root: {
                    "& .MuiOutlinedInput-root": {
                        borderRadius: 8,
                        backgroundColor: "#f3f3f5",
                        "& fieldset": {
                            borderColor: "rgba(0, 0, 0, 0.1)"
                        },
                        "&:hover fieldset": {
                            borderColor: "#103699"
                        },
                        "&.Mui-focused fieldset": {
                            borderColor: "#103699",
                            borderWidth: 2
                        }
                    }
                }
            }
        },
        // Divider styling
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: "rgba(0, 0, 0, 0.1)"
                }
            }
        },
        // Tooltip styling for accessibility
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: "#000000",
                    color: "#ffffff",
                    fontSize: "0.75rem",
                    borderRadius: 4,
                    padding: "8px 12px"
                },
                arrow: {
                    color: "#000000"
                }
            }
        },
        // AppBar/Toolbar styling
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: "#103699",
                    color: "#ffffff",
                    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)"
                }
            }
        },
        // Chip styling
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 16,
                    fontSize: "0.75rem",
                    fontWeight: 500
                },
                filled: {
                    backgroundColor: "#E3F2FD",
                    color: "#103699",
                    "&:hover": {
                        backgroundColor: "#B3D9F7"
                    }
                },
                outlined: {
                    borderColor: "#103699",
                    color: "#103699",
                    "&:hover": {
                        backgroundColor: "rgba(16, 54, 153, 0.04)"
                    }
                }
            }
        },
        // List item styling
        MuiListItem: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    marginBottom: 4,
                    "&:hover": {
                        backgroundColor: "rgba(16, 54, 153, 0.04)"
                    },
                    "&.Mui-selected": {
                        backgroundColor: "#E3F2FD",
                        color: "#103699",
                        "&:hover": {
                            backgroundColor: "#B3D9F7"
                        }
                    }
                }
            }
        },
        // Accordion styling
        MuiAccordion: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    border: "1px solid rgba(0, 0, 0, 0.1)",
                    boxShadow: "none",
                    "&:before": {
                        display: "none"
                    },
                    "&.Mui-expanded": {
                        margin: 0
                    }
                }
            }
        }
    }
});

export default muiTheme;
